
import React, { useState, useEffect } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Package, AlertCircle } from "lucide-react";
import { OrderProduct } from "@/hooks/useOrderProducts";

export interface SelectedOrderProduct extends OrderProduct {
  productMessage: string;
}

interface OrderProductSelectorProps {
  products: OrderProduct[];
  isLoading: boolean;
  error: string | null;
  selectedProducts: SelectedOrderProduct[];
  isOverarching: boolean;
  onToggleProduct: (product: OrderProduct) => void;
  onProductMessageChange: (sku: string, message: string) => void;
  onOverarchingChange: (value: boolean) => void;
}

const OrderProductSelector = ({
  products,
  isLoading,
  error,
  selectedProducts,
  isOverarching,
  onToggleProduct,
  onProductMessageChange,
  onOverarchingChange,
}: OrderProductSelectorProps) => {
  const [overarchingText, setOverarchingText] = useState("");

  const handleOverarchingMessageChange = (value: string) => {
    setOverarchingText(value);

    if (value.trim() && !isOverarching) {
      onOverarchingChange(true);
    }
    if (!value.trim() && isOverarching) {
      onOverarchingChange(false);
    }

    selectedProducts.forEach(p => {
      onProductMessageChange(p.sku, value);
    });
  };

  // Sync when products are selected while overarching text exists
  useEffect(() => {
    if (isOverarching && overarchingText.trim()) {
      selectedProducts.forEach(p => {
        onProductMessageChange(p.sku, overarchingText);
      });
    }
  }, [selectedProducts.length]);

  // Reset local state when isOverarching is turned off externally
  useEffect(() => {
    if (!isOverarching) {
      setOverarchingText("");
    }
  }, [isOverarching]);

  if (isLoading) {
    return (
      <div className="space-y-2 mt-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-destructive text-sm mt-3">
        <AlertCircle className="h-4 w-4" />
        {error}
      </div>
    );
  }

  if (products.length === 0) return null;

  const isSelected = (sku: string) =>
    selectedProducts.some((p) => p.sku === sku);

  const getMessage = (sku: string) =>
    selectedProducts.find((p) => p.sku === sku)?.productMessage || "";

  const hasSelectedProducts = selectedProducts.length > 0;

  return (
    <div className="space-y-3 mt-3">
      <Label className="text-sm font-medium flex items-center gap-1.5">
        <Package className="h-4 w-4" />
        Producten in order ({products.length})
      </Label>

      <div className="border rounded-lg divide-y max-h-[320px] overflow-y-auto">
        {products.map((product) => {
          const selected = isSelected(product.sku);
          return (
            <div key={product.sku} className="p-3 space-y-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={selected}
                  onCheckedChange={() => onToggleProduct(product)}
                  className="mt-1"
                />
                {product.imageUrl && (
                  <img
                    src={product.imageUrl}
                    alt={product.productTitle}
                    className="h-10 w-10 rounded object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {product.productTitle}
                  </p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                    {product.variantTitle && <span>{product.variantTitle}</span>}
                    <span>SKU: {product.sku}</span>
                    {product.ean && <span>EAN: {product.ean}</span>}
                    <span>Aantal: {product.quantity}</span>
                    {product.color && <span>Kleur: {product.color}</span>}
                  </div>
                </div>
              </div>
              {selected && !isOverarching && (
                <Input
                  placeholder="Melding voor dit product"
                  value={getMessage(product.sku)}
                  onChange={(e) =>
                    onProductMessageChange(product.sku, e.target.value)
                  }
                  className="ml-8 text-sm"
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="space-y-2">
        <Label htmlFor="overarching-message" className="text-sm font-medium">
          Overkoepelende melding
        </Label>
        <Textarea
          id="overarching-message"
          placeholder="Typ hier een melding die geldt voor alle geselecteerde producten..."
          value={overarchingText}
          onChange={(e) => handleOverarchingMessageChange(e.target.value)}
          rows={3}
          className="text-sm"
        />
        {isOverarching && (
          <p className="text-xs text-muted-foreground">
            Deze melding wordt gebruikt voor alle geselecteerde producten.
          </p>
        )}
      </div>
    </div>
  );
};

export default OrderProductSelector;
